import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import ConfirmationModal from '../components/ConfirmationModal';
import { Youtube as YoutubeIcon, Plus, Trash2, GripVertical, Play } from 'lucide-react';
import { Reorder, useDragControls } from 'framer-motion';
import { getYoutubePlaylist, updateYoutubePlaylist } from '../utils/api';

const getYouTubeID = (url) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

const VideoItem = ({ video, index, removeVideo }) => {
  const dragControls = useDragControls();
  const videoId = getYouTubeID(video.url);
  const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;

  return (
    <Reorder.Item
      value={video}
      dragListener={false}
      dragControls={dragControls}
      className="bg-[#1C1C1E] border border-[#2C2C2E] rounded-[32px] p-4 flex items-center gap-4 group cursor-auto touch-pan-y"
    >
      <div
        onPointerDown={(e) => {
          e.preventDefault();
          dragControls.start(e);
        }}
        className="text-slate-600 p-2 cursor-grab active:cursor-grabbing select-none touch-none"
      >
        <GripVertical size={20} />
      </div>

      <div className="w-20 h-12 rounded-xl bg-[#121214] text-white flex items-center justify-center shrink-0 overflow-hidden border border-[#2C2C2E] relative">
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt="Thumbnail" className="w-full h-full object-cover" />
        ) : (
          <Play size={20} fill="currentColor" />
        )}
      </div>

      <div className="flex-1 overflow-hidden">
        <h4 className="font-bold text-sm md:text-base truncate">
          {video.title || video.url}
        </h4>
        <p className="text-[10px] text-slate-500 font-bold truncate tracking-tight">
          {video.url}
        </p>
      </div>

      <button
        onClick={() => removeVideo(video.id)}
        className="p-2 text-slate-600 hover:text-red-500 transition-colors"
      >
        <Trash2 size={20} />
      </button>
    </Reorder.Item>
  );
};

const Youtube = () => {
  const [url, setUrl] = useState('');
  const [videos, setVideos] = useState([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadVideos = async () => {
      try {
        const response = await getYoutubePlaylist();
        const parsedVideos = response.data.playlist || [];
        setVideos(parsedVideos);
        
        // Check for videos missing titles and try to fetch them
        const missingTitles = parsedVideos.filter(v => !v.title);
        if (missingTitles.length > 0) {
          let needsSave = false;
          const updated = await Promise.all(parsedVideos.map(async (v) => {
            if (!v.title) {
              try {
                const title = await fetchVideoTitle(v.url);
                if (title) {
                  needsSave = true;
                  return { ...v, title };
                }
              } catch (e) {
                // If it fails on initial load check, just keep the video but maybe tag it as restricted in future
                console.warn(`Failed to fetch title for ${v.url}: ${e.message}`);
              }
            }
            return v;
          }));
          
          if (needsSave) {
            saveVideos(updated);
          }
        }
      } catch (err) {
        console.error('Failed to load videos from API:', err);
        // Fallback to localStorage if API fails? 
        // Or maybe just show an error. 
        // Given the requirement is to move to DB, we should rely on API.
      }
    };
    loadVideos();
  }, []);

  const saveVideos = async (newVideos) => {
    setVideos(newVideos);
    try {
      await updateYoutubePlaylist(newVideos);
    } catch (err) {
      console.error('Failed to save videos to API:', err);
      setError('Failed to sync playlist with server');
    }
  };

  const fetchVideoTitle = async (videoUrl) => {
    try {
      // First try directly with YouTube oEmbed to see if it's restricted or not found
      const youtubeOembed = `https://www.youtube.com/oembed?url=${encodeURIComponent(videoUrl)}&format=json`;
      const ytResponse = await fetch(youtubeOembed);
      
      if (!ytResponse.ok) {
        if (ytResponse.status === 401 || ytResponse.status === 403) {
          throw new Error('This video is restricted and cannot be embedded on other websites.');
        } else if (ytResponse.status === 404) {
          throw new Error('Video not found. Please check the URL.');
        } else {
          // If it's another error, we try noembed as a fallback (it might work for some)
          const response = await fetch(`https://noembed.com/embed?url=${encodeURIComponent(videoUrl)}`);
          const data = await response.json();
          if (data.error) throw new Error(data.error);
          return data.title || null;
        }
      }

      const data = await ytResponse.json();
      return data.title || null;
    } catch (error) {
      console.error('Error fetching video title:', error);
      throw error; // Rethrow to catch in addVideo
    }
  };

  const addVideo = async (e) => {
    e.preventDefault();
    setError('');
    const videoUrl = url.trim();
    if (!videoUrl) return;

    const videoId = getYouTubeID(videoUrl);
    if (!videoId) {
      setError('Please enter a valid YouTube URL');
      return;
    }

    try {
      const title = await fetchVideoTitle(videoUrl);

      const newVideo = {
        id: Date.now().toString(),
        url: videoUrl,
        title: title
      };

      const updatedVideos = [...videos, newVideo];
      saveVideos(updatedVideos);
      setUrl('');
    } catch (err) {
      setError(err.message || 'Failed to fetch video information. It might be restricted.');
    }
  };

  const removeVideo = (id) => {
    setVideoToDelete(id);
    setShowConfirmDelete(true);
  };

  const confirmRemoveVideo = () => {
    const updatedVideos = videos.filter((v) => v.id !== videoToDelete);
    saveVideos(updatedVideos);
    setShowConfirmDelete(false);
    setVideoToDelete(null);
  };

  const handleReorder = (newOrder) => {
    saveVideos(newOrder);
  };

  return (
    <Layout title="YOUTUBE ENTERTAINMENT" subtitle="Training session content">
      <div className="max-w-2xl mx-auto space-y-8">
        {/* Input Section */}
        <div className="bg-[#1C1C1E] rounded-[40px] p-8 border border-[#2C2C2E]">
          <h2 className="text-2xl font-black italic uppercase tracking-tighter mb-6 flex items-center gap-3">
            <div className="relative flex items-center justify-center">
              <YoutubeIcon className="text-[#A3E635]" size={32} />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-0 h-0 border-t-[5px] border-t-transparent border-l-[8px] border-l-[#1C1C1E] border-b-[5px] border-b-transparent ml-1"></div>
              </div>
            </div>
            Embed a Video
          </h2>
          
          <form onSubmit={addVideo} className="flex gap-4">
            <input
              type="text"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                if (error) setError('');
              }}
              placeholder="Paste YouTube URL here..."
              className={`flex-1 bg-[#121214] border ${error ? 'border-red-500' : 'border-[#2C2C2E]'} rounded-full px-6 py-4 text-white placeholder:text-slate-600 focus:outline-none focus:border-[#A3E635] transition-colors font-bold`}
            />
            <button
              type="submit"
              disabled={!url.trim()}
              className="bg-[#A3E635] text-black px-6 py-4 rounded-full font-black flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-50 disabled:hover:scale-100"
            >
              <Plus size={24} strokeWidth={3} />
            </button>
          </form>
          {error && (
            <p className="text-red-500 text-xs font-bold mt-4 px-6 uppercase tracking-wider italic">
              {error}
            </p>
          )}
        </div>

        {/* List Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-4">
            <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">
              Video Playlist ({videos.length})
            </h3>
          </div>

          {videos.length > 0 ? (
            <Reorder.Group
              axis="y"
              values={videos}
              onReorder={handleReorder}
              className="space-y-3"
            >
              {videos.map((video, index) => (
                <VideoItem
                  key={video.id}
                  video={video}
                  index={index}
                  removeVideo={removeVideo}
                />
              ))}
            </Reorder.Group>
          ) : (
            <div className="bg-[#1C1C1E] rounded-[32px] p-12 border border-dashed border-[#2C2C2E] text-center">
              <p className="text-slate-500 font-bold uppercase tracking-widest text-sm">
                No videos added yet
              </p>
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        onConfirm={confirmRemoveVideo}
        title="Remove Video?"
        message="Are you sure you want to remove this video from your playlist?"
      />
    </Layout>
  );
};

export default Youtube;
